import {createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState} from "react";
import {useAssignmentState} from "./AssignmentProdiver";
import {activePendingActions} from "../utils/assignment/PendingAssignment";

interface DispatchNotesContextValue {
  openIdentifier: string | null;
  getNoteText: () => string;
  getHadFocus: () => boolean;
  getSelection: () => [number, number];
  setOpen: (identifier: string) => void;
  setNoteText: (text: string) => void;
  setHadFocus: (value: boolean) => void;
  setSelection: (start: number, end: number) => void;
  clear: () => void;
  isPending: (identifier: string) => boolean;
}

const defaultContext: DispatchNotesContextValue = {
  openIdentifier: null,
  getNoteText: () => "",
  getHadFocus: () => false,
  getSelection: () => [0, 0],
  setOpen: () => {},
  setNoteText: () => {},
  setHadFocus: () => {},
  setSelection: () => {},
  clear: () => {},
  isPending: () => false
};

const DispatchNotesContext = createContext<DispatchNotesContextValue>(defaultContext);

export const useDispatchNotesContext = () => useContext(DispatchNotesContext);

export const DispatchNotesProvider = ({children}: {children: ReactNode}) => {
  const {pendingActions} = useAssignmentState();
  const [openIdentifier, setOpenIdentifier] = useState<string | null>(null);
  const noteTextRef = useRef("");
  const hadFocusRef = useRef(false);
  const selectionRef = useRef<[number, number]>([0, 0]);

  const setOpen = useCallback((identifier: string) => {
    setOpenIdentifier(identifier);
    noteTextRef.current = "";
    hadFocusRef.current = false;
    selectionRef.current = [0, 0];
  }, []);

  const setNoteText = useCallback((text: string) => {
    noteTextRef.current = text;
  }, []);

  const getNoteText = useCallback(() => noteTextRef.current, []);

  const setHadFocus = useCallback((value: boolean) => {
    hadFocusRef.current = value;
  }, []);

  const getHadFocus = useCallback(() => hadFocusRef.current, []);

  const setSelection = useCallback((start: number, end: number) => {
    selectionRef.current = [start, end];
  }, []);

  const getSelection = useCallback((): [number, number] => selectionRef.current, []);

  const clear = useCallback(() => {
    setOpenIdentifier(null);
    noteTextRef.current = "";
    hadFocusRef.current = false;
    selectionRef.current = [0, 0];
  }, []);

  const isPending = useCallback(
    (identifier: string) => {
      const active = activePendingActions(pendingActions);
      const [type, id] = identifier.split("-");
      const numId = Number(id);
      return active.some((pa) => (type === "job" ? pa.jobIds.includes(numId) : pa.jobStopIds.includes(numId)));
    },
    [pendingActions]
  );

  const value = useMemo(
    () => ({
      openIdentifier,
      getNoteText,
      getHadFocus,
      getSelection,
      setOpen,
      setNoteText,
      setHadFocus,
      setSelection,
      clear,
      isPending
    }),
    [
      openIdentifier,
      getNoteText,
      getHadFocus,
      getSelection,
      setOpen,
      setNoteText,
      setHadFocus,
      setSelection,
      clear,
      isPending
    ]
  );

  return <DispatchNotesContext.Provider value={value}>{children}</DispatchNotesContext.Provider>;
};
